import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  FileText, DollarSign, TrendingUp, Download, RefreshCw, 
  Upload, PieChart, BarChart3, Calendar, BookOpen, FileSpreadsheet, Eye, Settings
} from 'lucide-react';
import FinanceDocuments from './finance/FinanceDocuments';
import FinanceTransactions from './finance/FinanceTransactions';
import FinanceReports from './finance/FinanceReports';
import FinanceExports from './finance/FinanceExports';
import ParsedTransactions from './finance/ParsedTransactions';
import Ledger from './finance/Ledger';
import JournalEntries from './finance/JournalEntries';
import VendorMappings from './finance/VendorMappings';

export default function FinanceDashboard() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('documents');
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());
  const [displayMonth, setDisplayMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      // First try current month
      let year = new Date().getFullYear();
      let month = new Date().getMonth() + 1;
      
      let res = await api.get(`/finance/reports/monthly?year=${year}&month=${month}`);
      
      // If current month has no data, try previous months
      if (res.data.transaction_count === 0 && res.data.ledger_count === 0) {
        // Try last 6 months to find data
        for (let i = 1; i <= 6; i++) {
          month--;
          if (month === 0) {
            month = 12;
            year--;
          }
          res = await api.get(`/finance/reports/monthly?year=${year}&month=${month}`);
          if (res.data.transaction_count > 0 || res.data.ledger_count > 0) {
            break;
          }
        }
      }
      
      setDisplayYear(year);
      setDisplayMonth(month);
      setSummary(res.data);
    } catch (error) {
      console.error('Failed to fetch finance summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Finance</h1>
          <p className="text-slate-500">Financial document management and reporting</p>
        </div>
        <Button onClick={fetchSummary} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Quick Summary Cards */}
      {!loading && summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">
                    {monthNames[displayMonth]} {displayYear} Income
                  </p>
                  <p className="text-2xl font-bold text-emerald-600">
                    ${summary.total_income?.toLocaleString() || '0'}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">
                    {monthNames[displayMonth]} {displayYear} Expenses
                  </p>
                  <p className="text-2xl font-bold text-red-600">
                    ${summary.total_expenses?.toLocaleString() || '0'}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card className={summary.net_profit >= 0 ? 'border-blue-200 bg-blue-50' : 'border-amber-200 bg-amber-50'}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Net Profit/Loss</p>
                  <p className={`text-2xl font-bold ${summary.net_profit >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                    ${summary.net_profit?.toLocaleString() || '0'}
                  </p>
                </div>
                <BarChart3 className={`h-8 w-8 ${summary.net_profit >= 0 ? 'text-blue-400' : 'text-amber-400'}`} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Transactions</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {(summary.ledger_count || 0) + (summary.manual_count || 0) || summary.transaction_count || 0}
                  </p>
                </div>
                <FileText className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-0">
            <TabsList className="grid w-full grid-cols-8">
              <TabsTrigger value="documents" className="flex items-center gap-1 text-xs">
                <FileText className="h-3 w-3" />
                <span className="hidden sm:inline">Docs</span>
              </TabsTrigger>
              <TabsTrigger value="parsed" className="flex items-center gap-1 text-xs">
                <Eye className="h-3 w-3" />
                <span className="hidden sm:inline">Review</span>
              </TabsTrigger>
              <TabsTrigger value="ledger" className="flex items-center gap-1 text-xs">
                <BookOpen className="h-3 w-3" />
                <span className="hidden sm:inline">Ledger</span>
              </TabsTrigger>
              <TabsTrigger value="journal" className="flex items-center gap-1 text-xs">
                <FileSpreadsheet className="h-3 w-3" />
                <span className="hidden sm:inline">Journal</span>
              </TabsTrigger>
              <TabsTrigger value="transactions" className="flex items-center gap-1 text-xs">
                <DollarSign className="h-3 w-3" />
                <span className="hidden sm:inline">Manual</span>
              </TabsTrigger>
              <TabsTrigger value="reports" className="flex items-center gap-1 text-xs">
                <PieChart className="h-3 w-3" />
                <span className="hidden sm:inline">Reports</span>
              </TabsTrigger>
              <TabsTrigger value="exports" className="flex items-center gap-1 text-xs">
                <Download className="h-3 w-3" />
                <span className="hidden sm:inline">Export</span>
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-1 text-xs">
                <Settings className="h-3 w-3" />
                <span className="hidden sm:inline">Settings</span>
              </TabsTrigger>
            </TabsList>
          </CardHeader>

          <CardContent className="pt-6">
            <TabsContent value="documents" className="mt-0">
              <FinanceDocuments />
            </TabsContent>

            <TabsContent value="parsed" className="mt-0">
              <ParsedTransactions />
            </TabsContent>

            <TabsContent value="ledger" className="mt-0">
              <Ledger />
            </TabsContent>

            <TabsContent value="journal" className="mt-0">
              <JournalEntries />
            </TabsContent>

            <TabsContent value="transactions" className="mt-0">
              <FinanceTransactions />
            </TabsContent>

            <TabsContent value="reports" className="mt-0">
              <FinanceReports />
            </TabsContent>

            <TabsContent value="exports" className="mt-0">
              <FinanceExports />
            </TabsContent>

            <TabsContent value="settings" className="mt-0">
              <VendorMappings />
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>
    </div>
  );
}
